import {
  getFormatter,
  InstrumentCategoryEnum,
  InstrumentStoreItem,
  InstrumentStorePrice,
  isDefined,
  isMarketOrderType,
  Loadable,
  LoadableState,
  orderTypesWhenPriceRequired,
  useInstrumentStoreItemSelector,
} from '@eih-netbroker/common';
import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../../shared/components/button/Button';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { instrumentStore } from '../../../../../shared/data/instrumentStore';
import { useEStream } from '../../../../../shared/services/sse/useEStream';
import { useDebounce } from '../../../../../shared/utils/useDebounce';
import { useLogger } from '../../../../../shared/utils/useLogger';
import { useFuturesInfo } from '../../../services/order/useFuturesInfo';
import { useCreateNormalOrder } from '../../../services/order/useNormalOrderView';
import { useOrderState } from '../../OrderStateContext';
import { OrderFormData } from './OrderForm2';
import { OrderFormNumberInput, OrderFormNumberInputProps, OrderFormNumberInputSubRow } from './OrderFormNumberInput';
import classes from './OrderFormQuantityInput.module.scss';

type OrderFormQuantityInputProps = Omit<OrderFormNumberInputProps, 'actionSlot' | 'onChange' | 'isLoading'> & {
  handleChange: <T extends keyof OrderFormData>(key: T, value: OrderFormData[T]) => void;
  order: OrderFormData;
};

const unknownValue = '-';

const selectPrice = (item: InstrumentStoreItem | null) => item?.price;
const priceEqual = (a?: InstrumentStorePrice, b?: InstrumentStorePrice) => a?.value === b?.value && a?.type === b?.type;

export const OrderFormQuantityInput = memo(
  ({ disabled, order, handleChange, readOnly, ...rest }: OrderFormQuantityInputProps) => {
    const { translation } = useLocale();
    const { availableBalance } = useOrderState();
    const { futuresInitialMargin } = useFuturesInfo();
    const { getMaxQuantity, isMaxQuantityLoading } = useCreateNormalOrder();
    const { infoSubRows, errorText } = useOrderFormQuantityInputSubRows({ order });

    const handleQuantityChange = useCallback(
      (value: OrderFormData['quantity']) => {
        handleChange('quantity', value);
      },
      [handleChange],
    );

    const handleOnMaxClick = useCallback(async () => {
      if (
        order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures &&
        availableBalance.isLoaded &&
        futuresInitialMargin.isLoaded
      ) {
        if (futuresInitialMargin.futuresInitialMargin === 0) {
          return;
        }

        const quantity = Math.floor(availableBalance.value / futuresInitialMargin.futuresInitialMargin);
        handleChange('quantity', quantity);
        return;
      }

      const quantity = await getMaxQuantity(order);
      handleChange('quantity', quantity);
    }, [availableBalance, futuresInitialMargin, getMaxQuantity, handleChange, order]);

    const maxButton = (
      <NbButton
        color="secondary"
        isLoading={isMaxQuantityLoading}
        type="button"
        className={classes.maxButton}
        onClick={handleOnMaxClick}
        disabled={disabled || isMaxQuantityLoading || readOnly}
        data-testid="order-form-quantity-max-button"
      >
        {translation.orderModal.max}
      </NbButton>
    );

    return (
      <OrderFormNumberInput
        {...rest}
        value={order.quantity}
        disabled={disabled || isMaxQuantityLoading}
        actionSlot={maxButton}
        subRows={infoSubRows}
        errorText={errorText}
        onChange={handleQuantityChange}
        readOnly={readOnly}
      />
    );
  },
);

type OrderFormQuantityInputSubRowsProps = {
  order: OrderFormData;
};

const useOrderFormQuantityInputSubRows = ({ order }: OrderFormQuantityInputSubRowsProps) => {
  const { getOrderTotalPrice, getFuturesOrderTransactionInfo } = useOrderState();
  const { formatCurrency } = getFormatter();
  const [transactionValue, setTransactionValue] = useState<LoadableState<string>>(Loadable.uninitialized);
  const { translation, translateWithParams } = useLocale();
  const { subscribeToInstrumentChanges } = useEStream();
  const livePrice = useInstrumentStoreItemSelector(
    order.instrument ?? null,
    instrumentStore,
    subscribeToInstrumentChanges,
    selectPrice,
    priceEqual,
    { source: 'OrderFormQuantityInput' },
  );
  const [futuresValue, setFuturesValue] = useState<
    LoadableState<{ marginRequirementSum: string; netValueSum: string }>
  >(Loadable.uninitialized);
  const { debounce, cancelDebounce } = useDebounce<void>(() => {}, 500);
  const isMarketOrder = isMarketOrderType(order.orderType);
  const effectivePrice = useMemo(
    () => (isMarketOrder ? livePrice?.value : order.price),
    [isMarketOrder, livePrice, order.price],
  );
  const logger = useLogger('OrderFormQuantityInput');
  const prevOrderTypeRef = useRef(order.orderType);

  const updateTransactionValue = useCallback(async () => {
    if (!isDefined(order.instrument)) {
      setTransactionValue(Loadable.loaded(unknownValue));
      setFuturesValue(Loadable.loaded({ marginRequirementSum: unknownValue, netValueSum: unknownValue }));
      return;
    }

    if (
      !isDefined(order.orderType) ||
      !isDefined(order.quantity) ||
      (!isDefined(effectivePrice) && orderTypesWhenPriceRequired.includes(order.orderType))
    ) {
      setTransactionValue(Loadable.uninitialized);
      setFuturesValue(Loadable.uninitialized);
      return;
    }

    if (
      order.quantity <= 0 ||
      (isDefined(effectivePrice) && orderTypesWhenPriceRequired.includes(order.orderType) && effectivePrice <= 0)
    ) {
      setTransactionValue(Loadable.loaded(unknownValue));
      setFuturesValue(Loadable.loaded({ marginRequirementSum: unknownValue, netValueSum: unknownValue }));
      return;
    }

    if (order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
      setFuturesValue(Loadable.loading);
      try {
        const futuresInfo = await getFuturesOrderTransactionInfo({
          quantity: order.quantity,
          instrument: order.instrument,
          orderType: order.orderType,
          price: effectivePrice,
        });

        if (!isDefined(futuresInfo)) {
          return Loadable.loading;
        }

        setFuturesValue(
          Loadable.loaded({
            marginRequirementSum: formatCurrency(futuresInfo.marginRequirementSum, order.instrument),
            netValueSum: formatCurrency(futuresInfo.netValueSum, order.instrument),
          }),
        );
      } catch (error) {
        logger.error('Failed to fetch futures order transaction info', error);
        setFuturesValue(Loadable.loaded({ marginRequirementSum: unknownValue, netValueSum: unknownValue }));
      }
    } else {
      setTransactionValue(Loadable.loading);

      try {
        const transactionInfo = await getOrderTotalPrice({
          quantity: order.quantity,
          instrument: order.instrument,
          orderType: order.orderType,
          price: effectivePrice,
        });
        setTransactionValue(
          Loadable.loaded({
            value: formatCurrency(transactionInfo, order.instrument),
          }),
        );
      } catch (error) {
        logger.error('Failed to fetch order total price', error);
        setTransactionValue(Loadable.loaded(unknownValue));
      }
    }
  }, [
    formatCurrency,
    getFuturesOrderTransactionInfo,
    getOrderTotalPrice,
    order.instrument,
    order.orderType,
    effectivePrice,
    order.quantity,
    logger,
  ]);

  useEffect(() => {
    const orderTypeChanged = prevOrderTypeRef.current !== order.orderType;
    prevOrderTypeRef.current = order.orderType;

    cancelDebounce();

    if (!isMarketOrder || orderTypeChanged) {
      updateTransactionValue();
    } else {
      debounce(async () => {
        await updateTransactionValue();
      });
    }
  }, [updateTransactionValue, cancelDebounce, debounce, isMarketOrder, order.orderType]);

  const infoSubRows = useMemo<OrderFormNumberInputSubRow[]>(() => {
    if (order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
      return [
        {
          id: 'futures-margin-requirement',
          content: (
            <NbIndefiniteLoaderValueWrapper
              showLoaderOnUninitialized
              loadableValue={futuresValue}
              loadedValueCb={futuresValue =>
                translateWithParams(translation.orderModal.futuresMarginRequirementSum, {
                  value: futuresValue.marginRequirementSum,
                })
              }
            />
          ),
        },
        {
          id: 'futures-net-value-sum',
          content: (
            <NbIndefiniteLoaderValueWrapper
              showLoaderOnUninitialized
              loadableValue={futuresValue}
              loadedValueCb={futuresValue =>
                translateWithParams(translation.orderModal.futuresNetValue, {
                  value: futuresValue.netValueSum,
                })
              }
            />
          ),
        },
      ];
    } else {
      return [
        {
          id: 'transaction-value',
          content: (
            <NbIndefiniteLoaderValueWrapper
              showLoaderOnUninitialized
              loadableValue={transactionValue}
              loadedValueCb={transactionValue => {
                if (isMarketOrder && transactionValue.value !== unknownValue) {
                  return `~ ${transactionValue.value}`;
                } else {
                  return transactionValue.value;
                }
              }}
            />
          ),
        },
      ];
    }
  }, [
    order.instrument?.instrumentCategory,
    futuresValue,
    transactionValue,
    isMarketOrder,
    translation,
    translateWithParams,
  ]);

  const errorText = useMemo<ReactNode>(() => {
    if (!isDefined(order.instrument)) {
      return undefined;
    }

    // Should not happen, but just in case
    if (!isDefined(order.quantity)) {
      return translation.orderModal.errors.quantityInput.quantityEmpty;
    }

    if (order.quantity <= 0) {
      return translation.orderModal.errors.quantityInput.quantityIsZero;
    }

    return undefined;
  }, [order.instrument, order.quantity, translation]);

  return { infoSubRows, errorText };
};
